import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Briefcase, Loader2, DollarSign, CheckCircle2, Clock, RefreshCw, Download, Receipt, AlertTriangle, Flag } from 'lucide-react';
import BookingViolationModal from './BookingViolationModal';

/**
 * PO-2026-06 — Complete Job & Payment History for a talent (admin/accounting).
 * Pulls the FULL booking/job/payment record + earned/paid/outstanding summary,
 * with filters (date, client, payment status, search). Payout = approved hours
 * x TALENT pay rate (never client bill rate).
 */
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const PAY_TONE = {
  paid: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  awaiting_readiness: 'bg-blue-100 text-blue-700',
  ready: 'bg-cyan-100 text-cyan-700',
  failed: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-slate-100 text-slate-500',
  no_show: 'bg-rose-50 text-rose-600',
  timesheet_rejected: 'bg-rose-50 text-rose-600',
  declined: 'bg-slate-100 text-slate-500',
  released: 'bg-slate-100 text-slate-500',
  withdrawn: 'bg-slate-100 text-slate-500',
};

const PAY_OPTIONS = ['paid', 'pending', 'awaiting_readiness', 'ready', 'failed'];

const TalentJobHistory = ({ talentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [f, setF] = useState({ date_from: '', date_to: '', client_id: '', payment_status: '', q: '' });
  // 2026-09 — per-booking Code of Conduct violation map + click-through modal.
  const [violMap, setViolMap] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);

  const loadViolations = useCallback(async () => {
    try {
      const r = await api.get(`/admin/talent/${talentId}/coc-violations`);
      const map = {};
      (r.data?.violations || []).forEach((v) => {
        if (!v.booking_id) return;
        if (!map[v.booking_id]) map[v.booking_id] = { total: 0, open: 0 };
        map[v.booking_id].total += 1;
        if (v.status === 'open') map[v.booking_id].open += 1;
      });
      setViolMap(map);
    } catch (e) { /* silent */ }
  }, [talentId]);

  useEffect(() => { if (talentId) loadViolations(); }, [talentId, loadViolations]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get(`/admin/talent/${talentId}/job-history`, { params });
      setData(r.data);
      if (!params.client_id) {
        const seen = {};
        (r.data.jobs || []).forEach((j) => { if (j.client_id) seen[j.client_id] = j.client_name; });
        setClients(Object.entries(seen).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (_) {
      setData({ jobs: [], count: 0, summary: {} });
    } finally { setLoading(false); }
  }, [talentId, f]);

  useEffect(() => { load(); }, [talentId]); // eslint-disable-line

  const s = data?.summary || {};

  const csvCell = (v) => {
    const str = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const exportCsv = () => {
    const jobs = data?.jobs || [];
    const headers = ['Event', 'Client', 'Date Worked', 'Role', 'Scheduled Hours', 'Actual Hours', 'Pay Rate', 'Wages', 'Reimbursement', 'Status', 'Payment Status', 'Date Paid', 'Reference', 'Data Gap'];
    const lines = [headers.map(csvCell).join(',')];
    jobs.forEach((j) => {
      lines.push([
        j.event_name, j.client_name, j.date_worked || '', j.role || '',
        j.scheduled_hours ?? '', j.actual_hours ?? '', j.pay_rate ?? '', j.total_amount ?? '',
        j.reimbursement ?? 0, j.status || '', j.display_status || j.payment_status || '',
        j.date_paid ? j.date_paid.slice(0, 10) : '', j.reference || '',
        j.data_gap ? 'MISSING HOURS' : '',
      ].map(csvCell).join(','));
    });
    lines.push('');
    lines.push(['Total earned (wages)', s.total_earned ?? 0].map(csvCell).join(','));
    lines.push(['Total paid', s.total_paid ?? 0].map(csvCell).join(','));
    lines.push(['Total outstanding', s.total_outstanding ?? 0].map(csvCell).join(','));
    lines.push(['Total reimbursements', s.total_reimbursement ?? 0].map(csvCell).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-payment-history-${talentId}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const stat = (label, value, Icon, tint) => (
    <div className={`rounded-xl border p-3 flex items-center gap-3 ${tint}`} data-testid={`job-history-${label}`}>
      <Icon className="w-5 h-5 shrink-0" />
      <div>
        <div className="text-lg font-bold">{money(value)}</div>
        <div className="text-[11px] opacity-80">{label}</div>
      </div>
    </div>
  );

  return (
    <Card data-testid="talent-job-history-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-cyan-600" /> Job &amp; Payment History
          </h3>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={loading || !(data?.jobs || []).length} className="h-8 text-xs gap-1.5" data-testid="job-history-export-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="job-history-refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {stat('Total earned', s.total_earned, DollarSign, 'bg-slate-50 border-slate-200 text-slate-800')}
          {stat('Total paid', s.total_paid, CheckCircle2, 'bg-emerald-50 border-emerald-200 text-emerald-800')}
          {stat('Total outstanding', s.total_outstanding, Clock, 'bg-amber-50 border-amber-200 text-amber-800')}
          {stat('Reimbursements', s.total_reimbursement, Receipt, 'bg-indigo-50 border-indigo-200 text-indigo-800')}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="w-auto h-9" data-testid="job-history-date-from" />
          <span className="text-gray-400 text-sm">to</span>
          <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="w-auto h-9" data-testid="job-history-date-to" />
          <Select value={f.payment_status || 'all'} onValueChange={(v) => setF({ ...f, payment_status: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-44 h-9" data-testid="job-history-status-filter"><SelectValue placeholder="Payment status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {PAY_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          {clients.length > 0 && (
            <Select value={f.client_id || 'all'} onValueChange={(v) => setF({ ...f, client_id: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-48 h-9" data-testid="job-history-client-filter"><SelectValue placeholder="Client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input placeholder="Search event or client…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="w-52 h-9" data-testid="job-history-search" />
          <Button size="sm" onClick={load} className="bg-cyan-600 hover:bg-cyan-700 text-white h-9" data-testid="job-history-apply">Apply</Button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
        ) : (data?.jobs || []).length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm" data-testid="job-history-empty">No jobs found for these filters.</div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm" data-testid="job-history-table">
              <thead>
                <tr className="text-left text-[11px] uppercase text-gray-400 border-b">
                  <th className="p-2">Event</th><th className="p-2">Client</th><th className="p-2">Date</th>
                  <th className="p-2">Role</th><th className="p-2 text-right">Sched</th><th className="p-2 text-right">Actual</th>
                  <th className="p-2 text-right">Rate</th><th className="p-2 text-right">Wages</th><th className="p-2 text-right">Reimb.</th>
                  <th className="p-2">Payment</th><th className="p-2">Paid</th><th className="p-2">Reference</th>
                </tr>
              </thead>
              <tbody>
                {data.jobs.map((j) => {
                  const viol = violMap[j.booking_id];
                  return (
                  <tr
                    key={j.booking_id}
                    onClick={() => setSelectedJob(j)}
                    className={`border-b border-gray-100 hover:bg-cyan-50/60 cursor-pointer ${j.data_gap ? 'bg-amber-50/60' : ''}`}
                    data-testid={`job-history-row-${j.booking_id}`}
                    title="Open event to review or flag a Code of Conduct violation"
                  >
                    <td className="p-2 font-medium text-gray-900 max-w-[180px] truncate">
                      <span className="text-cyan-700 hover:underline">{j.event_name}</span>
                      {viol && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle" data-testid={`job-history-coc-flag-${j.booking_id}`}>
                          <Badge className={`text-[9px] border-0 ${viol.open > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`} title={`${viol.total} Code of Conduct violation(s), ${viol.open} open`}>
                            <Flag className="w-2.5 h-2.5 mr-0.5" />CoC {viol.open > 0 ? viol.open : viol.total}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-gray-600 max-w-[120px] truncate">{j.client_name}</td>
                    <td className="p-2 text-gray-600 whitespace-nowrap">{fmtDate(j.date_worked)}</td>
                    <td className="p-2 text-gray-600">{j.role || '—'}</td>
                    <td className="p-2 text-right text-gray-500">{j.scheduled_hours ?? '—'}</td>
                    <td className="p-2 text-right text-gray-700">
                      {j.data_gap ? (
                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium" title="Timesheet approved but worked hours are missing — wages can't be computed. Chase the timesheet." data-testid={`job-history-gap-${j.booking_id}`}>
                          <AlertTriangle className="w-3.5 h-3.5" /> missing
                        </span>
                      ) : (j.actual_hours ?? '—')}
                    </td>
                    <td className="p-2 text-right text-gray-700">{money(j.pay_rate)}</td>
                    <td className="p-2 text-right font-semibold text-gray-900">{money(j.total_amount)}</td>
                    <td className="p-2 text-right text-indigo-700">{j.reimbursement > 0 ? money(j.reimbursement) : '—'}</td>
                    <td className="p-2"><Badge className={`${PAY_TONE[(j.display_status || j.payment_status || '').toLowerCase()] || 'bg-slate-100 text-slate-600'} border-0 text-[10px]`}>{(j.display_status || j.payment_status || 'pending').replace(/_/g, ' ')}</Badge></td>
                    <td className="p-2 text-gray-500 whitespace-nowrap text-xs">{j.date_paid ? fmtDate(j.date_paid.slice(0, 10)) : '—'}</td>
                    <td className="p-2 text-gray-500 text-xs max-w-[130px] truncate" title={j.reference || ''}>{j.reference || '—'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <BookingViolationModal
        open={!!selectedJob}
        job={selectedJob}
        talentId={talentId}
        onClose={() => setSelectedJob(null)}
        onChanged={loadViolations}
      />
    </Card>
  );
};

export default TalentJobHistory;
